import { DripsyFinalTheme, makeTheme } from 'dripsy';
import { merge } from 'lodash';
import { baseTheme } from '../../design-system/baseTheme/baseTheme';
import { palette } from './palette';

const baseColors = {
  $black: '#000000',
  $white: '#FFFFFF',
  // Primary
  $primary: palette.P2.$800,
  $secondary: palette.P1.$700,
  $accent: palette.P2.$300,
  // Text
  $textPrimary: palette.Black.$1000,
  $textSecondary: palette.Black.$800,
  $textTheme: palette.P2.$400,
  $textThemeSecondary: palette.P1.$700,
  $textDisable: palette.Black.$700,
  $textButtonPrimary: palette.P2.$1000,
  $textButtonSecondary: palette.Black.$1000,
  $textButtonOutline: palette.P2.$900,
  $textDarkBackground: palette.White.$700,
  $textDarkBackgroundSecondary: palette.White.$1000,
  $textTooltip: palette.White.$1000,
  $textDateDisabled: palette.Black.$600,
  $textAlertDefault: palette.Black.$1000,
  $textAlertContrast: palette.Black.$1000,
  $textHighlight: palette.P1.$800,
  $textModalHeader: palette.P2.$300,
  $textTitle: palette.P2.$500,
  // Icon
  $iconDefault: palette.Black.$800,
  $iconFocused: palette.P1.$300,
  // Border
  $borderPrimary: palette.Black.$400,
  $borderButton: palette.Black.$600, // #CCCCCC
  $borderPageItem: palette.Black.$300, // #F0F0F0
  $borderDivider: palette.Black.$500, // #D8D8D8
  // Background
  $backgroundPrimary: palette.White.$700,
  $backgroundSecondary: palette.Dark.$100,
  $backgroundInputActive: palette.P1.$700,
  $backgroundPopup: palette.White.$700,
  $backgroundTheme: palette.P1.$100,
  // Line Separator
  $lineThin: palette.Dark.$700,
  $lineThick: palette.Black.$1000,
  // Status
  $success: palette.Green.$600,
  $danger: palette.Red.$700,
  $warning: palette.Background.YELLOW_BG,
  $info: palette.Background.BLUE_BG,
  $disable: palette.Black.$300,
  // OTHERS
  $hyperlink: palette.P2.$600,
  $textNavigationActive: palette.P2.$700,
  // Pie Chart
  $pieChart0: palette.P1.$1000,
  $pieChart1: palette.P1.$800,
  $pieChart2: palette.P1.$700,
  $pieChart3: palette.P1.$600,
  // Skeleton
  $skeleton: palette.Black.$600,
  // Alerts
  $alertDanger: palette.Red.$100,
  $alertWarning: '#FFF7D4',
  // Date Picker
  $datePickerSelected: palette.P2.$800,
  $datePickerSelectedText: palette.White.$700,
  $datePickerHighlightedBackground: palette.P2.$400,
  $datePickerHighlightedText: palette.Black.$1000,
  $datePickerDisabledText: palette.Black.$600,
  $datePickerText: palette.Black.$1000,
  // Tags
  $tagSubscriptionBackgroundColor: '#E6F7FF',
  $tagSubscriptionColor: '#096DD9',
  $tagRedemptionBackgroundColor: '#E8F5E9',
  $tagRedemptionColor: '#388E3C',
  // Filter
  $filterSelected: palette.P2.$200,
  // Alert
  $alertErrorBackground: palette.Red.$300,
  $alertErrorText: palette.Black.$1000,
  $alertErrorTitle: palette.Red.$800,
  // Navbar
  $navbarTextActive: palette.P1.$700,
  $navbarTextInactive: palette.Dark.$600
};

const baseLinearGradients = {
  primaryDefault: [palette.P2.$300, palette.P2.$500],
  primaryPressed: [palette.P2.$500, palette.P2.$400],
  secondary1Default: [palette.P2.$300, palette.P2.$300],
  secondary1Pressed: [palette.P2.$400, palette.P2.$400],
  secondary2Default: [palette.Black.$300, palette.Black.$300],
  secondary2Pressed: [palette.P2.$400, palette.P2.$400],
  outlineDefault: ['transparent', 'transparent'],
  outlinePressed: ['transparent', 'transparent'],
  disabledDefault: [palette.Black.$300, palette.Black.$300],
  disabledPressed: [palette.Black.$300, palette.Black.$300],
  menuUnderline: ['#FFFFFF80', 'transparent'],
  menuUnderlineDark: ['#00000060', 'transparent'],
  SegmentSelectActive: [palette.P1.$500, palette.P1.$800],
  // LEGACY
  primaryButtonDefault: [palette.P2.$800, palette.P2.$700],
  primaryButtonPressed: [palette.P2.$500, palette.P2.$400],
  secondary1ButtonDefault: [palette.P2.$400, palette.P2.$400],
  secondary1ButtonPressed: [palette.P2.$300, palette.P2.$300],
  secondary2ButtonDefault: [palette.Black.$300, palette.Black.$300],
  secondary2ButtonPressed: [palette.P2.$400, palette.P2.$400],
  outlineButtonDefault: ['transparent', 'transparent'],
  outlineButtonPressed: ['transparent', 'transparent'],
  buttonDisabled: [palette.Black.$300, palette.Black.$300]
};

const colors = {
  light: baseColors,
  dark: {
    ...baseColors,
    $accent: palette.P2.$1000,
    // Text
    $textPrimary: palette.White.$700,
    $textSecondary: palette.Dark.$400,
    $textDisable: palette.Black.$600,
    $textButtonOutline: palette.P2.$800,
    $textAlertDefault: palette.White.$700,
    $textAlertContrast: palette.Black.$1000,
    $textDarkBackground: palette.White.$700,
    // Icon
    $iconDefault: palette.Black.$800,
    $iconFocused: palette.P1.$300,
    // Border
    $borderPrimary: palette.Dark.$700,
    $borderPageItem: palette.Black.$900, // #333333
    $borderDivider: palette.Dark.$700, // #D8D8D8
    $borderButton: palette.Black.$700, // #D8D8D8
    // Background
    $backgroundPrimary: palette.Dark.$1000,
    $backgroundSecondary: palette.Dark.$900,
    $backgroundPopup: palette.Dark.$900,
    // Line Separator
    $lineThin: palette.Dark.$700,
    $lineThick: palette.Black.$1000,
    // Status
    $success: palette.Green.$600,
    $disable: palette.Dark.$700,
    // Alerts
    $alertDanger: palette.Red.$800,
    $alertWarning: '#FFDB7D',
    // Date Picker
    $datePickerSelected: palette.P2.$800,
    $datePickerSelectedText: palette.White.$700,
    $datePickerHighlightedBackground: '#544A3C30',
    $datePickerHighlightedText: palette.P2.$700,
    $datePickerDisabledText: palette.P2.$500,
    $datePickerText: palette.White.$700
  }
};

const base = {
  ...baseTheme,
  colors: baseColors,
  linearGradients: baseLinearGradients
};

const rawLightTheme = { ...base };

const light = makeTheme(rawLightTheme);

const dark = merge({}, light, {
  colors: colors.dark,
  linearGradients: {
    ...baseLinearGradients,
    disabledDefault: [palette.Dark.$700, palette.Dark.$700],
    disabledPressed: [palette.Dark.$700, palette.Dark.$700]
  }
});

const lightTheme = makeTheme({
  ...light,
  types: {
    reactNativeTypesOnly: true,
    onlyAllowThemeValues: {
      space: 'always'
    }
  }
});

const darkTheme: typeof light = {
  ...dark
};

export const theme = {
  light: lightTheme as DripsyFinalTheme,
  dark: darkTheme as DripsyFinalTheme
};
